import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { IdVerificationStatus } from '@/lib/idVerification';

export interface IdVerificationState {
  status: IdVerificationStatus;
  identityVerifiedAt: string | null;
  latestRejectReason: string | null;
  latestRejectNotes: string | null;
}

export function useIdVerification() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const query = useQuery<IdVerificationState>({
    queryKey: ['id-verification', user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('id_verification_status, identity_verified_at')
        .eq('user_id', user!.id)
        .maybeSingle();

      let latestRejectReason: string | null = null;
      let latestRejectNotes: string | null = null;
      if (profile?.id_verification_status === 'rejected') {
        const { data: latest } = await supabase
          .from('id_verification_requests')
          .select('reject_reason, reject_notes')
          .eq('user_id', user!.id)
          .eq('status', 'rejected')
          .order('reviewed_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        latestRejectReason = latest?.reject_reason ?? null;
        latestRejectNotes = latest?.reject_notes ?? null;
      }

      return {
        status: (profile?.id_verification_status as IdVerificationStatus) ?? 'none',
        identityVerifiedAt: profile?.identity_verified_at ?? null,
        latestRejectReason,
        latestRejectNotes,
      };
    },
    staleTime: 60_000,
  });

  return {
    ...query,
    invalidate: () => qc.invalidateQueries({ queryKey: ['id-verification', user?.id] }),
  };
}
